import React from 'react';
import { BELT_COLORS } from '../../utils/belt-colors';
import { BeltConfig } from '../../types/belt';

export interface VerticalBeltProps {
  /**
   * Belt color (white, blue, purple, brown, black, etc.)
   */
  color?: string;

  /**
   * Number of stripes on the belt (0-10)
   */
  stripes?: number;

  /**
   * Full belt configuration object (takes precedence over color/stripes)
   */
  beltConfig?: BeltConfig;

  /**
   * Optional custom classNames for styling
   */
  className?: string;

  /**
   * Whether to show the belt component
   */
  visible?: boolean;

  /**
   * Optional size variant
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Optional belt rank text to display
   */
  rankText?: string;
}

/**
 * Get hex color value from color name
 */
const getColorHex = (colorName?: string): string => {
  if (!colorName) return BELT_COLORS.white.backgroundColorCode;

  const lowerName = colorName.toLowerCase();

  if (BELT_COLORS[lowerName]) {
    return BELT_COLORS[lowerName].backgroundColorCode;
  }

  if (lowerName.startsWith('#')) {
    return lowerName;
  }

  return BELT_COLORS.white.backgroundColorCode;
};

/**
 * VerticalBelt component that displays a vertical martial arts belt
 *
 * Supports:
 * - All 9 belt colors from original app
 * - Stripes (0-10) with configurable colors
 * - Contrast patterns (alternate, horizontal stripes, etc.) for special belts
 * - Full BeltConfig object for custom belts
 */
const VerticalBelt: React.FC<VerticalBeltProps> = ({
  color = 'white',
  stripes = 0,
  beltConfig,
  className = '',
  visible = true,
  size = 'medium',
  rankText,
}) => {
  // Don't render if not visible
  if (!visible) return null;

  // Extract values from beltConfig or props
  const baseColor = beltConfig?.baseColor || color;
  const stripeCount = beltConfig?.stripeCount ?? stripes;
  const contrastColor = beltConfig?.contrastColor;
  const contrastType = beltConfig?.contrastType;
  const stripeColor = beltConfig?.stripeColor || 'white';
  const stripeBgColor = beltConfig?.stripeBgColor || (baseColor.toLowerCase() === 'black' ? 'red' : 'black');

  // Get color hex values
  const baseColorHex = getColorHex(baseColor);
  const contrastColorHex = contrastColor ? getColorHex(contrastColor) : 'transparent';
  const stripeColorHex = getColorHex(stripeColor);
  const stripeBgColorHex = getColorHex(stripeBgColor);

  // Size configurations
  const sizeConfig = {
    small: { width: 40, height: 128 },
    medium: { width: 56, height: 160 },
    large: { width: 80, height: 208 },
  };

  const { width, height } = sizeConfig[size];

  // Limit stripes to a maximum of 10
  const validStripes = Math.min(Math.max(0, stripeCount), 10);

  // Determine if white belt (needs border)
  const isWhiteBelt = baseColor.toLowerCase() === 'white';

  // Text color based on belt
  const textColorHex = BELT_COLORS[baseColor.toLowerCase()]?.textColorCode || '#FFFFFF';

  /**
   * Render contrast pattern for vertical orientation
   */
  const renderContrast = () => {
    if (!contrastType || !contrastColor) return null;

    switch (contrastType) {
      case 'alternate':
        // Horizontal bands for vertical belt (alternating)
        return (
          <div
            className="absolute inset-0 flex flex-col pointer-events-none"
            style={{ zIndex: 1 }}
          >
            {[0, 1, 2, 3].map(i => (
              <div
                key={`contrast-${i}`}
                className="flex-1"
                style={{
                  backgroundColor: i % 2 === 1 ? contrastColorHex : 'transparent',
                }}
              />
            ))}
          </div>
        );

      case 'centered horizontal stripe':
        // Vertical stripe through center for vertical belt
        return (
          <div
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{
              left: '35%',
              width: '30%',
              backgroundColor: contrastColorHex,
              zIndex: 1,
            }}
          />
        );

      case 'top horizontal stripe':
        // Left stripe for vertical belt
        return (
          <div
            className="absolute top-0 bottom-0 left-0 pointer-events-none"
            style={{
              width: '25%',
              backgroundColor: contrastColorHex,
              zIndex: 1,
            }}
          />
        );

      case '2x horizontal stripes':
        // Double vertical stripes
        return (
          <>
            <div
              className="absolute top-0 bottom-0 pointer-events-none"
              style={{
                left: '20%',
                width: '15%',
                backgroundColor: contrastColorHex,
                zIndex: 1,
              }}
            />
            <div
              className="absolute top-0 bottom-0 pointer-events-none"
              style={{
                right: '20%',
                width: '15%',
                backgroundColor: contrastColorHex,
                zIndex: 1,
              }}
            />
          </>
        );

      default:
        return null;
    }
  };

  /**
   * Render stripes section at top of vertical belt
   */
  const renderStripes = () => {
    if (validStripes === 0) return null;

    return (
      <div
        className="absolute left-0 right-0 top-2 flex justify-center items-center py-2"
        style={{
          backgroundColor: stripeBgColorHex,
          minHeight: 24,
          zIndex: 2,
        }}
      >
        <div className="flex space-x-1">
          {Array.from({ length: validStripes }).map((_, index) => (
            <div
              key={`stripe-${index}`}
              style={{
                height: 8,
                width: 12,
                backgroundColor: stripeColorHex,
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  // Display text for the belt
  const displayText = rankText || beltConfig?.rank || beltConfig?.name;

  return (
    <div
      className={`relative flex flex-col items-center ${className}`}
      aria-label={`${beltConfig?.name || baseColor} belt${beltConfig?.rank ? ` - ${beltConfig.rank}` : ''}${validStripes > 0 ? ` with ${validStripes} stripes` : ''}`}
    >
      {/* Top knot */}
      <div
        className="w-full rounded-t-sm"
        style={{
          height: size === 'large' ? 20 : size === 'medium' ? 14 : 10,
          backgroundColor: baseColorHex,
          border: isWhiteBelt ? '1px solid #d1d5db' : 'none',
          borderBottom: 'none',
        }}
      />

      {/* Main belt body */}
      <div
        className="relative w-full overflow-hidden"
        style={{
          width,
          height,
          backgroundColor: baseColorHex,
          border: isWhiteBelt ? '1px solid #d1d5db' : 'none',
          borderTop: 'none',
          borderBottom: 'none',
        }}
      >
        {/* Contrast pattern */}
        {renderContrast()}

        {/* Stripes section at top */}
        {renderStripes()}

        {/* Belt rank text if provided (displayed vertically) */}
        {displayText && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ zIndex: 3 }}
          >
            <span
              className="text-sm font-bold tracking-wide transform rotate-90 whitespace-nowrap"
              style={{ color: textColorHex }}
            >
              {displayText}
            </span>
          </div>
        )}
      </div>

      {/* Bottom knot */}
      <div
        className="w-full rounded-b-sm"
        style={{
          height: size === 'large' ? 20 : size === 'medium' ? 14 : 10,
          backgroundColor: baseColorHex,
          border: isWhiteBelt ? '1px solid #d1d5db' : 'none',
          borderTop: 'none',
        }}
      />
    </div>
  );
};

export default VerticalBelt;
